import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { TwoCol, SubSection } from "./fundCardHelpers";
import { InfoRow } from "../UI";

// ── תצוגה קומפקטית לקרן פנסיה ─────────────────────────────────────────────────
// בפנסיה יש כיסוי אחד (KisuiBKerenPensia) — מציגים הכל שטוח, בלי צורך להרחיב.

// שורות כ-data (לא כ-children) כדי שסקציה שכל שדותיה ריקים לא תוצג בכלל
function MiniSection({ title, rows }) {
  const visible = (rows || []).filter(r => {
    if (r.value == null || r.value === "") return false;
    if ((r.money || r.pct) && !r.value) return false;
    return true;
  });
  if (!visible.length) return null;
  return (
    <div style={{ background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>{title}</div>
      {visible.map((r, i) => (
        <InfoRow key={i} label={r.label} value={r.value} money={r.money} pct={r.pct} bold={r.bold} color={r.color} />
      ))}
    </div>
  );
}

// קוד מסלול לפי קידוד אחיד — הספרות המשמעותיות בסוף (למשל ...0000115 → 115)
function maslulShortCode(code) {
  if (!code || code.length < 7) return null;
  const n = parseInt(code.slice(-7), 10);
  return isNaN(n) || n === 0 ? null : n;
}

function PensionCoverageView({ fund, cs }) {
  const k = (fund.kisuim || []).find(x =>
    x.sacharKoveaNechutSheerim != null || x.sheurNechut != null ||
    x.kitzbatSheerim != null || x.alutNechut != null
  ) || (fund.kisuim || [])[0] || {};

  const maslulCode = maslulShortCode(fund.maslulBituachCode);
  const maslul = fund.maslulBituach
    ? `${fund.maslulBituach}${maslulCode ? ` (${maslulCode})` : ""}`
    : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10, marginBottom: 20 }}>
      <MiniSection title="פרטי ביטוח כלליים" rows={[
        { label: "תאריך ערך לנתונים", value: k.taarichErechLanetunim },
        { label: "עמית מבוטח בקרן פנסיה?", value: fund.mevutachBaKeren, bold: true },
        { label: "מסלול ביטוח", value: maslul },
      ]} />

      <MiniSection title="שכר קובע" rows={[
        { label: "שכר קובע לכיסוי נכות ושאירים", value: k.sacharKoveaNechutSheerim, money: true, bold: true, color: cs.accent },
        { label: "תאריך נכונות שכר קובע", value: k.taarichMaskoretNechutSheerim },
      ]} />

      <MiniSection title="עלויות כיסויים" rows={[
        { label: "עלות כיסוי נכות", value: k.alutNechut, money: true },
        { label: "עלות כיסוי שארים", value: k.alutSheerim, money: true },
        { label: "עלות כיסוי פנסיית שארים של נכה", value: k.alutPnsShrmNeche, money: true },
      ]} />

      <MiniSection title="שיעורי כיסוי" rows={[
        { label: "שיעור כיסוי נכות", value: k.sheurNechut, pct: true },
        { label: "שיעור כיסוי ביטוחי לאלמן/ה", value: k.sheurSheerim, pct: true },
        { label: "שיעור כיסוי ביטוחי ליתום", value: k.sheurYatom, pct: true },
        { label: "שיעור כיסוי ביטוחי להורה נתמך", value: k.sheurHoreNitmach, pct: true },
      ]} />

      <MiniSection title="קצבאות" rows={[
        { label: "סך פנסיית נכות (לפי נכות מלאה)", value: k.sachPensiatNechut, money: true, bold: true, color: cs.accent },
        { label: "קצבת שארים לאלמן/ה", value: k.kitzbatSheerim, money: true },
        { label: "קצבת שארים ליתום", value: k.kitzbatYatom, money: true },
        { label: "קצבת שארים להורה נתמך", value: k.kitzbatHoreNitmach, money: true },
      ]} />

      <MiniSection title="ויתורים" rows={[
        { label: "ויתור על כיסוי ביטוחי לנכות", value: k.viturKisuyBituchi },
        { label: "ויתור על כיסוי שארים", value: k.sugVitorShaerim },
        { label: "תאריך ויתור שארים", value: k.taarichViturSheerim },
        { label: "תום ויתור שארים", value: k.taarichSiumViturSheerim },
      ]} />

      <MiniSection title="פרטי חברות" rows={[
        { label: "גיל פרישה לפנסיית זקנה", value: k.gilPrishaKeren },
        { label: "תאריך תחילת חברות", value: k.taarichTchilatHaverut },
        { label: "חודשי חברות בקרן", value: k.hodsheiHaverut },
        { label: "חודשי חברות רצופים", value: k.hodsheiHaverutRetzufim },
      ]} />

      <MiniSection title="פרטים נוספים" rows={[
        { label: "קיימת הטבה ביטוחית", value: k.hatavaBituchit },
        { label: "נכות מתפתחת", value: k.nechutMitpatahatPensia },
        { label: "מנת פנסיה צבורה", value: k.menatPensia },
        { label: "אחוז פנסיה צבורה", value: k.ahuzPensiyaTzvura, pct: true },
      ]} />
    </div>
  );
}

function fmtYM(ym) {
  if (!ym) return "";
  const s = String(ym);
  if (s.startsWith("9999") || s.startsWith("2999")) return "הלאה";
  if (s.length === 6) return `${s.slice(4, 6)}/${s.slice(0, 4)}`;
  return s;
}

const handleExportPremia = (premiaZfoya, title) => {
  if (!premiaZfoya || premiaZfoya.length === 0) return;
  const data = premiaZfoya.map(pp => ({
    "מתאריך": fmtYM(pp.from),
    "עד תאריך": fmtYM(pp.to),
    "פרמיה (₪)": pp.amt
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "פרמיה עתידית");
  XLSX.writeFile(wb, `${title || 'premium'}_future.xlsx`);
};

function CollapsibleCoverage({ k, i, cs }) {
  const [isOpen, setIsOpen] = useState(false);
  const title = k.sugKisuy || k.name || `כיסוי ${i + 1}`;
  // הסכום פר-כיסוי (SCHUM-BITUACH); לכיסוי מוות יסודי — fallback לסכום מהבלוק היסודי
  const schum = k.schumBituach ?? k.bituachLemavet;

  return (
    <div style={{ marginBottom: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflow: "hidden" }}>
      {/* Header (always visible) */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{ 
          padding: "12px 16px", 
          cursor: "pointer", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          background: isOpen ? "#f1f5f9" : "transparent",
          transition: "background 0.2s"
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: cs.accent, marginBottom: 4 }}>
            {title}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#64748b" }}>
            {k.name && <span>{k.name}</span>}
            {schum && <span>סכום ביטוח: <strong style={{color:"#334155"}}>{schum?.toLocaleString()} ₪</strong></span>}
            {!schum && k.achuzMeSachar && <span>כיסוי: <strong style={{color:"#334155"}}>{k.achuzMeSachar}% מהשכר</strong></span>}
            {k.premium && <span>פרמיה: <strong style={{color:"#334155"}}>{k.premium} ₪</strong></span>}
            {k.sugKisuiYatzran && <span>{k.sugKisuiYatzran}</span>}
          </div>
        </div>
        <div style={{ fontSize: 14, color: "#94a3b8", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
          ▼
        </div>
      </div>

      {/* Body (collapsible) */}
      {isOpen && (
        <div style={{ padding: "16px", borderTop: "1px solid #e2e8f0" }}>
          {/* מידע כללי */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>מידע כללי ורפואי</div>
            <TwoCol>
              <div>
                <InfoRow label="שם כיסוי אצל היצרן" value={k.name} />
                <InfoRow label="תחילת כיסוי" value={k.startDate} />
                <InfoRow label="תום כיסוי" value={k.endDate} />
                <InfoRow label="הפסקת תשלום" value={k.stopPayDate} />
              </div>
              <div>
                <InfoRow label="סוג מבוטח" value={k.sugMevutach} />
                <InfoRow label="ת.ז. מבוטח" value={k.mevutach?.id} />
                <InfoRow label="סוג עיסוק" value={k.sugIsuk} />
                <InfoRow label="מקצוע / תחביב" value={k.miktsoa?.tchumIsuk || k.miktsoa?.tachvivim} />
                <InfoRow label="סטטוס עישון" value={k.kodIshun} />
                <InfoRow label="משלם הכיסוי" value={k.meshalemHakisu} />
                <InfoRow label="סוג כיסוי אצל היצרן" value={k.sugKisuiYatzran} />
              </div>
            </TwoCol>
          </div>

          {/* סכומי ביטוח והצמדה */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>סכומי ביטוח והצמדות</div>
            <TwoCol>
              <div>
                <InfoRow label="סכום ביטוח" value={schum} money bold color={cs.accent} />
                <InfoRow label="סכום ביטוח למוות (יסודי)" value={k.schumBituach != null ? k.bituachLemavet : null} money />
                <InfoRow label="סכום ביטוח כולל חיסכון?" value={k.indKolelChisachon} />
                <InfoRow label='אכ"ע — % מהשכר' value={k.achuzMeSachar} pct />
                <InfoRow label='אכ"ע — סכום חודשי' value={k.schumSheur} money />
                <InfoRow label="% מסכום הביטוח היסודי" value={k.achuzMeSchumYesodi} pct />
                <InfoRow label="תקרת גג חתם" value={k.tikratGagMavet} money />
                <InfoRow label='תקרת גג חתם אכ"ע' value={k.tikratGagAka} money />
                <InfoRow label="אחוז הגדלת גג חתם" value={k.achuzHagdalatGag} pct />
              </div>
              <div>
                <InfoRow label="אופן תשלום סכום ביטוח" value={k.ofenTashlumSchum} />
                <InfoRow label="סוג הצמדת סכום ביטוח" value={k.sugHatzmadaSchum} />
                <InfoRow label="סוג הצמדת דמי ביטוח" value={k.sugHatzmadaDmei} />
                <InfoRow label="האם קבוע/משתנה" value={k.schumBituhKavoaMishtane} />
                <InfoRow label="סוג מסלול ביטוח" value={k.sugMaslulLebituah} />
                <InfoRow label="מספר משכורות (כפולות שכר)" value={k.misparMaskorot} />
              </div>
            </TwoCol>
          </div>

          {/* פרמיות, חיתום והנחות */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>פרמיות, חיתום והנחות</div>
            <TwoCol>
              <div>
                <InfoRow label="פרמיה / דמי ביטוח" value={k.premium} money />
                <InfoRow label="עלות כיסוי" value={k.alutKisui} money />
                <InfoRow label="תוספת תעריף %" value={k.tosefetPct} pct />
                <InfoRow label="הנחה בכיסוי %" value={k.hanachaPct} pct />
                <InfoRow label="סוג הנחה" value={k.sugHanachaKisuy} />
                <InfoRow label="ערך הנחה" value={k.erechHanacha} money />
                <InfoRow label="תנאי הנחה" value={k.tnaeiHanacha} />
                <InfoRow label="הנחה בתוקף עד" value={k.hanachaEnd} />
                <InfoRow label="תדירות שינוי דמי ביטוח (שנים)" value={k.tadirutShinuyDmei} />
                <InfoRow label="תאריך עדכון דמי ביטוח" value={k.taarichIdkunDmeiBituah} />
              </div>
              <div>
                <InfoRow label="נערך חיתום?" value={k.indChitum} />
                <InfoRow label="תאריך חיתום" value={k.taarichChitum} />
                <InfoRow label="קיימת החרגה?" value={k.hachraga} />
                <InfoRow label="סוג החרגה" value={k.sugHachraga} />
                <InfoRow label="תקופת אכשרה (חודשים)" value={k.tkufatAchshara} />
                <InfoRow label="תקופת המתנה (חודשים)" value={k.tkufatHamtana} />
              </div>
            </TwoCol>
          </div>

          {/* נתוני אכ"ע ופנסיה */}
          {(k.kolelPrenzisa || k.kolelSiud || k.kitzbaZmudaLamadad || k.nachutMitpatahat || k.alutNechut || k.sheurNechut || k.kitzbatSheerim) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>נתוני אכ"ע ופנסיה</div>
              <TwoCol>
                <div>
                  <InfoRow label='אכ"ע כולל פרנציזה?' value={k.kolelPrenzisa} />
                  <InfoRow label='אכ"ע כולל סיעוד?' value={k.kolelSiud} />
                  <InfoRow label="קצבה צמודה למדד" value={k.kitzbaZmudaLamadad} />
                  <InfoRow label="נכות מתפתחת" value={k.nachutMitpatahat} />
                  <InfoRow label="ביטול קיזוז ממשלתי" value={k.bitulKizuzMemshalti} />
                </div>
                <div>
                  <InfoRow label="כיסוי נכות %" value={k.sheurNechut} pct />
                  <InfoRow label="עלות כיסוי נכות" value={k.alutNechut} money />
                  <InfoRow label="כיסוי שארים %" value={k.sheurSheerim} pct />
                  <InfoRow label="קצבת שארים" value={k.kitzbatSheerim} money />
                  <InfoRow label="כיסוי יתום %" value={k.sheurYatom} pct />
                </div>
              </TwoCol>
            </div>
          )}

          {/* תוספות חיתום */}
          {k.tosafot?.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: "#fff", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>🩺 תוספות חיתום</div>
              {k.tosafot.map((t, j) => (
                <div key={j} style={{ fontSize: 12, display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
                  {t.kod && <span style={{ fontWeight: 600 }}>{t.kod}</span>}
                  {t.sheur != null && <span>{t.sheur}%</span>}
                  {t.promil != null && <span>{t.promil}‰</span>}
                  {t.taarif != null && <span>{t.taarif.toLocaleString()} ₪</span>}
                  {t.endDate && <span style={{ color: "#94a3b8" }}>עד {t.endDate}</span>}
                </div>
              ))}
            </div>
          )}

          {/* התפתחות פרמיה */}
          {k.premiaZfoya?.length > 0 && (
            <div style={{ marginBottom: 12, padding: 12, background: "#fff", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>📈 התפתחות פרמיה עתידית</div>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleExportPremia(k.premiaZfoya, title); }}
                  style={{ fontSize: 11, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                  ייצוא לאקסל
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {k.premiaZfoya.map((pp, j) => (
                  <div key={j} style={{ fontSize: 12, background: "#f8fafc", padding: "4px 8px", borderRadius: 4, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "#64748b", direction: "ltr", display: "inline-block" }}>
                      {fmtYM(pp.from)} - {fmtYM(pp.to)}
                    </span>
                    <span style={{ fontWeight: 600, color: "#334155" }}>{pp.amt} ₪</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* התפתחות סכום ביטוח (למשל ריסק משכנתא יורד) */}
          {k.schumZafuy?.length > 0 && (
            <div style={{ marginBottom: 12, padding: 12, background: "#fff", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>📉 התפתחות סכום ביטוח צפוי</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {k.schumZafuy.map((pp, j) => (
                  <div key={j} style={{ fontSize: 12, background: "#f8fafc", padding: "4px 8px", borderRadius: 4, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "#64748b", direction: "ltr", display: "inline-block" }}>
                      {fmtYM(pp.from)} - {fmtYM(pp.to)}
                    </span>
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      {pp.schum != null ? `${pp.schum.toLocaleString()} ₪` : `${pp.sheur}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* הנחה מדורגת */}
          {k.hanachaMedureget?.length > 0 && (
            <div style={{ padding: 12, background: "#fff", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>🏷️ הנחה מדורגת</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {k.hanachaMedureget.map((pp, j) => (
                  <div key={j} style={{ fontSize: 12, background: "#f8fafc", padding: "4px 8px", borderRadius: 4, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "#64748b", direction: "ltr", display: "inline-block" }}>
                      {fmtYM(pp.from)} - {fmtYM(pp.to)}
                    </span>
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      {pp.sheur != null ? `${pp.sheur}%` : `${pp.schum?.toLocaleString()} ₪`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CoverageTab({ fund, cs }) {
  const kisuim = fund.kisuim || [];
  const gvia = fund.gvia || [];
  const tviot = fund.tviot || [];
  const mitriyot = fund.mitriyot || [];
  const hasPolicyInfo = gvia.length || tviot.length || mitriyot.length || fund.riskZmaniStart;

  if (!kisuim.length && (!fund.sheerim || !fund.sheerim.length) && !hasPolicyInfo) {
    return <div style={{ color: "#94a3b8", fontSize: 13 }}>אין כיסויים ביטוחיים בחשבון זה</div>;
  }

  return (
    <>
      {/* ריסק זמני */}
      {fund.riskZmaniStart && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fef9c3", borderRadius: 8, border: "1px solid #fde047", fontSize: 13, color: "#92400e" }}>
          ⚠️ ריסק זמני: {fund.riskZmaniStart}{fund.riskZmaniEnd ? ` — עד ${fund.riskZmaniEnd}` : ""}
        </div>
      )}

      {/* תביעות */}
      {tviot.length > 0 && (
        <SubSection title={`תביעות (${tviot.length})`} color={cs.accent}>
          {tviot.map((t, i) => (
            <div key={i} style={{ marginBottom: 8, padding: 10, background: "#fff1f2", borderRadius: 6, border: "1px solid #fecdd3" }}>
              <TwoCol>
                <div>
                  <InfoRow label="סוג תביעה" value={t.sugTvia} />
                  <InfoRow label="כיסוי" value={t.shemKisui || t.misparKisui} />
                  <InfoRow label="מספר תביעה" value={t.misparTvia} />
                  <InfoRow label="אופן תשלום" value={t.ofenTashlum} />
                </div>
                <div>
                  <InfoRow label="סטטוס" value={t.status} />
                  <InfoRow label="תאריך סטטוס" value={t.statusDate} />
                  <InfoRow label="תחילת תשלום" value={t.startPayDate} />
                  <InfoRow label="סכום מאושר" value={t.schumMeushar} money />
                  <InfoRow label='% אכ"ע / שחרור מאושר' value={t.achuzOka} pct />
                  <InfoRow label="% נכות" value={t.achuzNechut} pct />
                </div>
              </TwoCol>
            </div>
          ))}
        </SubSection>
      )}

      {fund.cat === "קרן פנסיה" && kisuim.length > 0 ? (
        // קרן פנסיה — תצוגה שטוחה וקומפקטית, בלי צורך להרחיב
        <PensionCoverageView fund={fund} cs={cs} />
      ) : (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
            נמצאו {kisuim.length} כיסויים ביטוחיים (לחץ על כיסוי לפירוט)
          </div>
          {kisuim.map((k, i) => (
            <CollapsibleCoverage key={i} k={k} i={i} cs={cs} />
          ))}
        </div>
      )}

      {/* כיסויים קולקטיביים (מטריות) */}
      {mitriyot.length > 0 && (
        <SubSection title="כיסויים קולקטיביים (מטריות)" color={cs.accent}>
          {mitriyot.map((m, i) => (
            <div key={i} style={{ marginBottom: 8, padding: 10, background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
              <TwoCol>
                <div>
                  <InfoRow label="סוג כיסוי" value={m.sugMutzar} />
                  <InfoRow label="מבטחת" value={m.mevatachat} />
                  <InfoRow label="תחילת ביטוח" value={m.startDate} />
                  <InfoRow label="תום ביטוח" value={m.endDate} />
                </div>
                <div>
                  <InfoRow label="סכום ביטוח" value={m.schum} money />
                  <InfoRow label="עלות כיסוי" value={m.alut} money />
                  <InfoRow label="משלם" value={m.meshalem} />
                  <InfoRow label="תדירות תשלום" value={m.tadirut} />
                  <InfoRow label="נחתם טופס הצטרפות?" value={m.nechtamTofes} />
                </div>
              </TwoCol>
            </div>
          ))}
        </SubSection>
      )}

      {/* פרטי גבייה */}
      {gvia.length > 0 && (
        <SubSection title="פרטי גבייה" color={cs.accent}>
          {gvia.map((gv, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <TwoCol>
                <div>
                  <InfoRow label="שם המשלם" value={gv.meshalem} />
                  <InfoRow label="מזהה משלם" value={gv.meshalemId ? `${gv.meshalemId}${gv.sugTeuda ? " (" + gv.sugTeuda + ")" : ""}` : null} />
                  <InfoRow label="אמצעי תשלום" value={gv.emtzaeiTashlum} />
                </div>
                <div>
                  <InfoRow label="תדירות תשלום" value={gv.tadirut} />
                  <InfoRow label="יום גבייה בחודש" value={gv.yomGviya} />
                  <InfoRow label="הצמדת גבייה" value={gv.hatzmada} />
                  <InfoRow label="% תת-שנתיות" value={gv.achuzTatShnatiyot} pct />
                </div>
              </TwoCol>
            </div>
          ))}
        </SubSection>
      )}

      {fund.sheerim?.length > 0 && (
        <SubSection title="שארים" color={cs.accent}>
          {fund.sheerim.map((s, i) => (
            <InfoRow key={i} label={s.zika || `שאיר ${i+1}`} value={`${s.name} ${s.bday ? "("+s.bday+")" : ""}`} />
          ))}
        </SubSection>
      )}
    </>
  );
}
